import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { AlertTriangle, Sparkles, Image as ImageIcon, MessageSquare } from 'lucide-react'
import type { NoteTemplate, Asset } from '@/types'

export interface TemplatePickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templates: NoteTemplate[]
  assets: Asset[]
  noteCount: number
  onConfirm: (templateId: string, avoidDuplicates: boolean, customInstruction: string) => void
  quota?: {
    remaining: number
    dailyLimit: number
    isNearLimit: boolean
    isExceeded: boolean
  }
}

/**
 * 模版选择弹窗组件
 */
export function TemplatePicker({
  open,
  onOpenChange,
  templates,
  assets,
  noteCount,
  onConfirm,
  quota,
}: TemplatePickerProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const [avoidDuplicates, setAvoidDuplicates] = useState<boolean>(false)
  const [customInstruction, setCustomInstruction] = useState<string>('')
  const [showWarning, setShowWarning] = useState<boolean>(false)

  const handleCancel = () => {
    onOpenChange(false)
    setSelectedTemplateId('')
    setAvoidDuplicates(false)
    setCustomInstruction('')
  }

  const handleConfirm = () => {
    if (!selectedTemplateId) {
      toast.error('请选择一个配图模版')
      return
    }

    // 检查素材是否充足
    if (avoidDuplicates) {
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId)
      if (selectedTemplate) {
        const availableAssets = assets.filter(a => 
          !a.usedInNoteIds?.length && a.analysis_status === 'completed'
        )
        const requiredCount = noteCount * selectedTemplate.image_count_min
        
        if (availableAssets.length < requiredCount) {
          setShowWarning(true)
          return
        }
      }
    }

    proceedWithConfirm()
  }

  const proceedWithConfirm = () => {
    onConfirm(selectedTemplateId, avoidDuplicates, customInstruction)
    onOpenChange(false)
    setSelectedTemplateId('')
    setAvoidDuplicates(false)
    setCustomInstruction('')
    setShowWarning(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" aria-describedby={undefined}>
        <DialogHeader className="px-6 py-4 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              智能配图配置
            </DialogTitle>
            {quota && (
              <div className={cn(
                "text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 font-medium",
                quota.isExceeded 
                  ? 'bg-red-50 border-red-200 text-red-700' 
                  : quota.isNearLimit 
                    ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                    : 'bg-blue-50 border-blue-200 text-blue-700'
              )}>
                <div className={cn(
                  "w-1.5 h-1.5 rounded-full animate-pulse",
                  quota.isExceeded ? "bg-red-500" : quota.isNearLimit ? "bg-yellow-500" : "bg-blue-500"
                )} />
                今日AI生图：{quota.remaining} / {quota.dailyLimit}
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* 任务摘要 */}
          <div className="bg-primary/5 rounded-lg p-3 flex items-center justify-between border border-primary/10">
            <div className="flex items-center gap-2 text-sm text-primary font-medium">
              <ImageIcon className="w-4 h-4" />
              待配图笔记数量
            </div>
            <div className="text-lg font-bold text-primary">
              {noteCount} <span className="text-xs font-normal opacity-70">篇</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">选择配图模版</Label>
              <span className="text-xs text-muted-foreground">共 {templates.length} 个模版可用</span>
            </div>
            
            {templates.length === 0 ? (
              <div className="py-12 text-center border-2 border-dashed rounded-xl bg-muted/20">
                <p className="text-muted-foreground">当前项目没有可用的配图模版</p>
                <p className="text-sm mt-1 opacity-70">请先在项目设置中配置风格和模版</p>
              </div>
            ) : (
              <RadioGroup
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
                className="grid gap-3"
              >
                {templates.map(template => (
                  <div
                    key={template.id}
                    className={cn(
                      'relative flex items-start space-x-3 rounded-xl border p-4 cursor-pointer transition-all duration-200 hover:shadow-sm',
                      selectedTemplateId === template.id
                        ? 'border-primary ring-1 ring-primary/20 bg-primary/[0.02]'
                        : 'border-border hover:border-primary/40 hover:bg-muted/30'
                    )}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    <RadioGroupItem value={template.id} id={template.id} className="mt-1" />
                    <div className="flex-1 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor={template.id}
                          className="font-bold text-base cursor-pointer"
                        >
                          {template.name}
                        </Label>
                        {selectedTemplateId === template.id && (
                          <div className="bg-primary text-white text-[10px] px-2 py-0.5 rounded-full">
                            当前选择
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {template.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                          配图数量: <span className="text-foreground font-medium">{template.image_count_min}-{template.image_count_max}</span> 张
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                          AI生成上限: <span className="text-foreground font-medium">{template.ai_image_max}</span> 张
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            )}
          </div>

          {/* 自定义指令 */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2">
              <Label className="text-base font-semibold">自定义指令 (可选)</Label>
              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground uppercase">Optional</span>
            </div>
            <div className="relative group">
              <div className="absolute top-3 left-3 text-muted-foreground/50 group-focus-within:text-primary/50 transition-colors">
                <MessageSquare className="w-4 h-4" />
              </div>
              <Textarea
                placeholder="例如：多使用室内背景、重点展示产品细节、或者指定某种色调等..."
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                className="min-h-[100px] pl-10 py-3 resize-none bg-muted/10 border-muted-foreground/20 focus:border-primary/50 focus:ring-primary/20 transition-all text-sm leading-relaxed"
              />
            </div>
          </div>

          {/* 策略设置 */}
          <div className="space-y-4 pt-2">
            <Label className="text-base font-semibold">生成策略</Label>
            <div className="bg-muted/30 rounded-xl border p-4 hover:bg-muted/40 transition-colors">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="avoid-duplicates" className="text-sm font-semibold cursor-pointer">
                      过滤已被使用的素材
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    开启后，系统会自动排除已分配给其他笔记的素材，确保内容不重复。
                  </p>
                </div>
                <Switch
                  id="avoid-duplicates"
                  checked={avoidDuplicates}
                  onCheckedChange={setAvoidDuplicates}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-muted/30">
          <Button variant="ghost" onClick={handleCancel} className="font-medium">
            取消
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!selectedTemplateId || quota?.isExceeded} 
            className="px-8 font-semibold shadow-sm"
          >
            {quota?.isExceeded ? '额度不足' : '立即开始配图'}
          </Button>
        </div>
      </DialogContent>

      {/* 素材不足警告对话框 */}
      <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              素材不足警告
            </AlertDialogTitle>
            <AlertDialogDescription>
              可用素材数量不足，继续生成将导致部分配图失败。
              <br />
              <br />
              <strong>建议：</strong>
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>关闭"过滤已使用素材"选项</li>
                <li>或上传更多素材</li>
                <li>或减少生成的笔记数量</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={proceedWithConfirm}>
              继续生成
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}

